// TODO:
// - currency and locale shoud be setup in config/env and read from there
// - replace currenctFormat.format() function with currencyFormatter(val, curr);
import {
	AdminApproved,
	CalculationRes,
	OfferPurpose,
	UnitType,
	Value,
	ValueType,
	Portfolio,
	IOptionMapped
} from 'api';
import { OfferAlertState } from '../components/Calculator/Partials/OfferSMEAlert';
import { CULTURE_INFO, CULTURE_CURRENCY } from '../config/global';

export const toNumber = (n: any) => (isNaN(n) || n === '' ? 0 : parseFloat(n));

export const dateFormat = (date: Date | number | string) =>
	new Date(date).toLocaleDateString(CULTURE_INFO);

export const currencyFormatter = (v: number | undefined = 0, c: string = CULTURE_CURRENCY) => {
	const value = v;

	if (isNaN(value) || value === 0) return '-';

	return new Intl.NumberFormat(CULTURE_INFO, {
		style: 'currency',
		currency: c,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	}).format(value);
};

export const currencyFormatterFraction = (
	v: number | undefined = 0,
	fractionSize: number = 0,
	showZero: boolean = true,
	c: string = CULTURE_CURRENCY
) => {
	const value = v;

	if (isNaN(value)) return '-';

	if (!showZero && value === 0) {
		return '-';
	}

	return new Intl.NumberFormat(CULTURE_INFO, {
		style: 'currency',
		currency: c,
		minimumFractionDigits: fractionSize,
		maximumFractionDigits: 2
	}).format(value);
};

export const currencyFormat = new Intl.NumberFormat(CULTURE_INFO, {
	style: 'currency',
	currency: CULTURE_CURRENCY,
	minimumFractionDigits: 0,
	maximumFractionDigits: 0

	// These options are needed to round to whole numbers if that's what you want.
	//minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
	//maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

// Scroll function for elements when using anchor tags with nav offset
export const scrollToTargetAdjusted = (elementId: string) => {
	var element = document.getElementById(elementId);
	var elementPosition = element?.getBoundingClientRect().top;
	var navOffset = document.getElementById('page-topbar')?.offsetHeight;
	var offsetPosition =
		(elementPosition ?? 0) - (navOffset && !isNaN(navOffset) ? navOffset * 1.2 : 0);
	window.scrollBy({
		top: offsetPosition,
		behavior: 'smooth'
	});
};

// returns uid
export const generateUniqueKey = () =>
	Date.now().toString(36) + Math.random().toString(36).substr(2);

// flattend nested object to dot annotation (eg.: {parent:{child:{}}} => {"parent.child":{}})
export const flattenObject = (ob: any) => {
	let toReturn: any = {};
	for (var i in ob) {
		if (!ob.hasOwnProperty(i)) continue;

		if (typeof ob[i] == 'object' && ob[i] !== null) {
			var flatObject = flattenObject(ob[i]);
			for (var x in flatObject) {
				if (!flatObject.hasOwnProperty(x)) continue;

				toReturn[i + '.' + x] = flatObject[x];
			}
		} else {
			toReturn[i] = ob[i];
		}
	}
	return toReturn;
};

/**
 * Moves an item in array to new index position.
 * @export
 * @param {Array<any>} arr
 * @param {number} indexOld
 * @param {number} indexNew
 * @return {*}
 */
export function moveInArray(arr: Array<any>, indexOld: number, indexNew: number) {
	while (indexOld < 0) {
		indexOld += arr.length;
	}
	while (indexNew < 0) {
		indexNew += arr.length;
	}
	if (indexNew >= arr.length) {
		var k = indexNew - arr.length + 1;
		while (k--) {
			arr.push(undefined);
		}
	}
	arr.splice(indexNew, 0, arr.splice(indexOld, 1)[0]);
	return arr; // for testing purposes
}

/**
 * If string is in array
 * @param {string} value - string value
 * @param {array} array - array of strings
 * @returns {boolean}
 */
export function isInArray(value: number | string, arr: Array<number | string>): boolean {
	try {
		if (arr.indexOf(value) > -1) {
			return true;
		} else {
			return false;
		}
	} catch (e) {
		return false;
	}
}

export function removeFromArray(arrayIn: Array<any>, target: any, multiple?: boolean): Array<any> {
	let arrayOut = arrayIn;
	if (multiple && Array.isArray(target)) {
		arrayOut = arrayOut.filter(function (e) {
			return target.indexOf(e) < 0;
		});
	} else {
		if (isInArray(target, arrayIn)) {
			arrayOut = [
				...arrayIn.slice(0, arrayIn.indexOf(target)),
				...arrayIn.slice(arrayIn.indexOf(target) + 1)
			];
		}
	}
	return arrayOut;
}

/**
 * Deep comparison of two elements
 * @param {*} original - original element to compare with
 * @param {*} changed - changed element to compare
 * @returns {boolean}
 */
export function ifEqual(original: any, changed: any): boolean {
	if (original === changed) return true;

	if (
		original == null ||
		typeof original != 'object' ||
		changed == null ||
		typeof changed != 'object'
	)
		return false;

	var propsInA = 0,
		propsInB = 0;

	for (var prop in original) propsInA += 1;

	for (prop in changed) {
		propsInB += 1;
		if (!(prop in original) || !ifEqual(original[prop], changed[prop])) return false;
	}

	return propsInA === propsInB;
}

/**
 * Checks if object is empty (doesn't contain any property)
 * @param obj - JS object
 * @returns {boolean}
 */
export function isObjectEmpty(obj: any): boolean {
	return Object.keys(obj).length === 0 && obj.constructor === Object;
}

export const isDefined = (variable: any): boolean => {
	if ([undefined, null, ''].includes(variable)) return false;
	else if (Array.isArray(variable) && variable.length === 0) return false;
	else if (typeof variable === 'object' && Object.keys(variable).length === 0) return false;
	return true;
};

export function CallMixUnitFormatter(unit: UnitType | undefined): string {
	if (unit === undefined) return '';
	if (unit === UnitType.No) return 'N°';
	return unit;
}

export function CallMixValueFormatter(
	value: number | undefined = 0,
	unit: UnitType | undefined
): string {
	if (isNaN(value) || value === 0) {
		return '-';
	}

	switch (unit) {
		case UnitType.Mou:
			return new Intl.NumberFormat(CULTURE_INFO, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value);
		case UnitType.No:
			return new Intl.NumberFormat(CULTURE_INFO, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value);
		case UnitType.Mb:
			return new Intl.NumberFormat(CULTURE_INFO, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}).format(value);
		default:
			return new Intl.NumberFormat(CULTURE_INFO, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			}).format(value);
	}
}

export const filterUniqueArray = (array: any[], key: string) => {
	if (!isDefined(array)) return array;

	return array.reduce(
		(prev, curr) =>
			prev.find((a: any) => a[key] === curr[key]) ? prev : prev.push(curr) && prev,
		[]
	);
};

export const groupBy = (items: any[], key: string) => {
	if (!isDefined(items)) return items;

	return items.reduce(
		(result, item) => ({
			...result,
			[item[key]]: [...(result[item[key]] || []), item]
		}),
		{}
	);
};

export const reduceOptionsPriceByVat = (options: IOptionMapped[], vatValue: number = 1) => {
	if (options && vatValue) {
		return options.map((opt: IOptionMapped) => {
			const optionPriceNoVat: number =
				opt && opt.price && vatValue ? opt.price / vatValue : opt.price || 0;

			return { ...opt, price: Number(optionPriceNoVat.toFixed(2)) };
		});
	}

	if (options) return options;

	return [];
};

export function isEmptyObject(obj: any) {
	return JSON.stringify(obj) === '{}';
}

export function deepCopy(obj: object) {
	return JSON.parse(JSON.stringify(obj));
}

export function isDecimalNumber(num: number) {
	return num - Math.floor(num) !== 0;
}

export const calculatePercent = (discountedValue: number, maxValue: number) => {
	if (discountedValue !== undefined && maxValue !== undefined) {
		return Number((100 - (discountedValue / maxValue) * 100).toFixed(2));
	} else {
		return 0;
	}
};

export const getValueTypeValue = (value: Value) => {
	if (!value) return;
	switch (value.valueType) {
		case ValueType.String:
			return value.valueString;
		case ValueType.Boolean:
			return value.valueBool;
		case ValueType.Enum:
			return value.valueEnum;
		case ValueType.Range:
			return value.valueRange;
		case ValueType.Date:
			return value.valueDate;
		default:
			return value.valueNumber;
	}
};

function approveAlertMessage(
	mustApproveOffer: boolean | undefined,
	approvalMessage: string,
	adminApproved: AdminApproved | undefined
) {
	// offerParams?.adminApproved ? 'Offer approved': calculation?.mustApproveOffer ? 'Offer needs approval': calculation?.message
	if (!mustApproveOffer) {
		return approvalMessage;
	} else if (mustApproveOffer && approvalMessage && !adminApproved) {
		return approvalMessage;
	}
	if (adminApproved) {
		switch (adminApproved) {
			case AdminApproved.Allow:
				return 'Offer approved';
			case AdminApproved.Continue:
				return 'Offer was audited';
			default:
				return 'Offer needs approval';
		}
	}
	return 'Offer needs approval';
}

function approveMessageState(
	mustApproveOffer: boolean | undefined,
	adminApproved: AdminApproved | undefined
) {
	// offerParams?.adminApproved ? false: calculation?.mustApproveOffer ? true: false
	if (!mustApproveOffer) {
		//  && (!adminApproved || adminApproved === AdminApproved.None)
		return OfferAlertState.Allowed;
	}

	if (adminApproved) {
		switch (adminApproved) {
			case AdminApproved.Allow:
				return OfferAlertState.Allowed;
			case AdminApproved.Continue:
				return OfferAlertState.Info;
			case AdminApproved.Disallow:
			default:
				return OfferAlertState.None;
		}
	}

	if (mustApproveOffer) {
		//  && (!adminApproved || adminApproved === AdminApproved.None)
		return OfferAlertState.None;
	}

	return OfferAlertState.None;
}

export function approveMessageData(calculation: CalculationRes, adminApproved?: AdminApproved) {
	if (!calculation || !adminApproved) {
		return {
			state: OfferAlertState.None,
			text: ''
		};
	}

	const { mustApproveOffer, message } = calculation;

	return {
		state: approveMessageState(mustApproveOffer, adminApproved),
		text: approveAlertMessage(mustApproveOffer, message!, adminApproved)
	};
}

export function checkHasCustomerData(configForm: any, customerForm: any, newOffer: boolean) {
	if (!configForm || !customerForm) return;

	const { ban, pib, companyName } = customerForm?.customerData;

	const offerPurpose = configForm.offerConfig.offerPurpose;
	const hasCompanyData = companyName !== '' && companyName !== undefined;
	const checkPib = isDefined(pib) && pib?.toString().length === 9;
	const checkBan = isDefined(ban) && ban?.toString().length === 9;

	if (newOffer) {
		if (offerPurpose === OfferPurpose.Renewal) {
			return checkBan && hasCompanyData;
		} else {
			return checkPib && hasCompanyData;
		}
	} else {
		return true;
	}
}

export function getFilteredPortfolios(
	portfolios: Portfolio[],
	offerPurpose: OfferPurpose
): Portfolio[] {
	return portfolios
		.filter((p: Portfolio) => p.offerPurpose?.includes(offerPurpose))
		.filter((p: Portfolio) => p.active === true);
}

export function generateRandomString(len: number) {
	var charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz+/';
	let result = '';

	while (len > 0) {
		var bytes = new Uint8Array(16);
		var random = window.crypto.getRandomValues(bytes);

		// eslint-disable-next-line no-loop-func
		random.forEach((char) => {
			if (len === 0) return;
			if (char < charset.length) {
				result += charset[char];
				len--;
			}
		});
	}
	return result;
}
